import * as CL from '@design-system/component-library';
import * as React from 'react';
import { BREAKPOINT_S } from '../../common/constants/breakpoints';
import { atContext, atType } from '../../common/constants/namingConventionVariables';
import { ElisaYrityspuheUusiOfferCode } from '../../common/enums';
import {
  activationFeeWithVarMsg,
  activationFeesWithVarMsg,
  changeFeeMsg,
  eurosPerMonthCapMsg,
  noFixedTermMsg,
  saveMsg,
  t,
  totalMsg,
} from '../../common/i18n';
import { createProductStructuredDataFromOnlineModel } from '../../common/structured-data/product';
import { formatSumToString } from '../../common/utils/priceUtils';
import { isInBrowser } from '../../common/utils/ssrUtils';
import { OnlineModelCategory } from '../../generated/api/onlineModelCategory';
import { addToShoppingBasket } from '../ShoppingBasket/shoppingBasketFunctions';
import { getAllOfferAddonCodes } from '../ShoppingBasket/shoppingBasketHelpers';
import { isSubCardWithImpression } from './subscriptionCardsAnalyticsUtils';
import type { SubscriptionCardWithModel } from './types';
import type { Offer } from '../../generated/api/offer';
import type { OnlineModel } from '../../generated/api/onlineModel';
import type { ItemList, WithContext } from 'schema-dts';

export const isMobileView = isInBrowser() && window.innerWidth < BREAKPOINT_S;

export const getOffer = (offerCode?: string, offers?: Offer[]) => offers?.find(o => o.offerCode === offerCode);

export const getLaitenettiOfferByName = (offerName?: string, offers?: Offer[]) =>
  offers?.find(o => o?.offerName === offerName);

export const getOfferByCommercialProductCode = (offers?: Offer[], commercialProductCode?: string) =>
  offers?.find(o => o?.commercialProducts?.some(cp => cp.commercialProductCode === commercialProductCode));

export interface PriceProps {
  monthly?: number;
  onetime?: number;
  inModal?: boolean;
  quantity?: number;
  pricePreamble?: string;
  saving?: string;
  isChangeOffer?: boolean;
  fixedTermMonths?: number;
  showFixedTermInfo?: boolean;
}

export const Price = ({
  monthly,
  onetime,
  inModal,
  quantity = 1,
  pricePreamble,
  saving,
  isChangeOffer,
  fixedTermMonths,
  showFixedTermInfo,
}: PriceProps) => {
  const additionalInfo = `${quantity > 1 ? t.HWDV(activationFeesWithVarMsg, formatSumToString(onetime, true)) : t.HWDU(activationFeeWithVarMsg, formatSumToString(onetime, true))} €, Alv 0%`;
  const serviceFeeInfo = `${t.ZRKX(changeFeeMsg)} ${formatSumToString(onetime, true)} €, Alv 0%`;
  const info = (
    <>
      {showFixedTermInfo && (
        <div>
          {fixedTermMonths ? t.IWHR('{} month fixed term contract', `${fixedTermMonths}`) : t.B10G(noFixedTermMsg)}.
        </div>
      )}
      <div>{isChangeOffer ? serviceFeeInfo : additionalInfo}</div>
    </>
  );

  return (
    <CL.Price
      currentPrice={`${inModal ? `${t.CEQ2(totalMsg)}: ` : ''}${formatSumToString((monthly || 0) * quantity, true)}`} // * quantity
      currentPriceUnit={t.H37G(eurosPerMonthCapMsg)}
      pricePreamble={pricePreamble}
      additionalInfo={info}
      size={inModal ? 's' : undefined}
      saving={saving}
    />
  );
};

export const hasDiscount = (subscriptionCard?: SubscriptionCardWithModel) =>
  (subscriptionCard?.originalMonthlyPrice ?? 0) > (subscriptionCard?.monthlyPrice ?? 0) ||
  (subscriptionCard?.originalOneTimePrice ?? 0) > (subscriptionCard?.oneTimePrice ?? 0);

const hasCampaignAssociation = (
  subscriptionCard?: SubscriptionCardWithModel
): subscriptionCard is SubscriptionCardWithModel & { campaignAssociation: unknown } =>
  Boolean(subscriptionCard && 'campaignAssociation' in subscriptionCard && subscriptionCard.campaignAssociation);

export const hasCampaignDiscount = (subscriptionCard?: SubscriptionCardWithModel) => {
  if (!hasDiscount(subscriptionCard)) {
    return false;
  }

  if (hasCampaignAssociation(subscriptionCard)) {
    return true;
  }

  const offer =
    subscriptionCard && 'offer' in subscriptionCard && subscriptionCard.offer
      ? subscriptionCard.offer
      : subscriptionCard?.onlineModel?.offers?.find(o => o.offerCode === subscriptionCard.selectedOffer);

  const commercialProduct = offer?.commercialProducts[0];
  if (!commercialProduct) {
    return false;
  }

  const cpHasCampaign = Boolean(commercialProduct.price?.campaign);

  const offerAddonCodes = commercialProduct.addOnAssociations
    ?.filter(a => a.isOfferAddOn !== false)
    .map(a => a.addOnCode);
  const bundledAddons =
    commercialProduct.associatedAddOns?.filter(addon => offerAddonCodes?.includes(addon.addOnCode)) ?? [];
  const addonHasCampaign = bundledAddons.some(addon => Boolean(addon.price?.campaign));

  return cpHasCampaign || addonHasCampaign;
};

export const getSaving = (subscriptionCard?: SubscriptionCardWithModel, quantity: number = 1) => {
  if (!subscriptionCard?.originalMonthlyPrice || !subscriptionCard?.monthlyPrice) {
    return undefined;
  }

  const { originalMonthlyPrice, monthlyPrice } = subscriptionCard;

  if (originalMonthlyPrice - monthlyPrice > 0) {
    return `${t.HWDT(saveMsg)} ${formatSumToString((originalMonthlyPrice - monthlyPrice) * quantity, false)}`;
  }

  return undefined;
};

export interface CarouselWrapperProps {
  children: React.ReactNode;
}

interface AddOfferToBasketParams {
  onlineModel: OnlineModel;
  quantity: number;
  isAuthenticated?: boolean;
  offerCode?: string;
  addonCode?: string;
  noeOfferCode?: string;
  bbOfferCode?: string;
  salesProductOfferCode?: string;
}

export const addOfferToBasket = ({
  onlineModel,
  quantity,
  isAuthenticated,
  offerCode,
  addonCode,
  noeOfferCode,
  bbOfferCode,
  salesProductOfferCode,
}: AddOfferToBasketParams) => {
  const resolvedOfferCode = offerCode ?? bbOfferCode ?? salesProductOfferCode ?? noeOfferCode;
  if (!onlineModel?.offers?.length) {
    return;
  }
  let offer;
  const isSalesProduct = onlineModel.category === OnlineModelCategory.SALES_PRODUCT;

  // SALES_PRODUCT models don't have addon associations, match by offerCode only
  if (isSalesProduct) {
    offer = onlineModel.offers.find(o => o.offerCode === resolvedOfferCode);
  } else if (addonCode) {
    offer = onlineModel.offers.find(o => {
      const hasMatchingCode = o.offerCode === resolvedOfferCode;
      const hasMatchingAddon = o.commercialProducts[0]?.addOnAssociations?.some(
        assoc => assoc.addOnCode === addonCode && assoc.addOnPurpose === 'MANDATORY'
      );
      return hasMatchingCode && hasMatchingAddon;
    });
  } else {
    offer = onlineModel.offers.find(o => o.offerCode === resolvedOfferCode);
  }

  if (offer) {
    const cp = offer.commercialProducts[0];
    const allOfferAddonCodes = getAllOfferAddonCodes(cp);
    addToShoppingBasket(onlineModel, offer, cp, Boolean(isAuthenticated), quantity, allOfferAddonCodes);
  }
};

export const MobileCarouselWrapper: React.FC<CarouselWrapperProps> = ({ children }) => {
  return isMobileView ? (
    <CL.Carousel buttons="primary" buttonLocation="top" snap="always">
      {children}
    </CL.Carousel>
  ) : (
    <>{children}</>
  );
};

export const getSubscription = (subscriptionCards: SubscriptionCardWithModel[], cardName: string) => {
  return subscriptionCards?.find(card => card.cardName === cardName);
};

export const getItemList = (
  subscriptions: SubscriptionCardWithModel[],
  siteBaseUrl: string,
  pathname: string
): WithContext<ItemList> => {
  // We need the onlineModel for the structured data.
  const items = subscriptions.filter(isSubCardWithImpression);
  return {
    [atContext]: 'https://schema.org',
    [atType]: 'ItemList',
    itemListElement: items
      .map((subData, idx) => {
        const structuredData = createProductStructuredDataFromOnlineModel(
          subData.onlineModel,
          siteBaseUrl,
          subData.offer.offerCode,
          subData.productName
        );
        return structuredData
          ? {
              ...structuredData,
              position: idx,
            }
          : undefined;
      })
      .filter(element => element != null),
    numberOfItems: items.length,
    url: `${siteBaseUrl}${pathname}`,
  };
};

export const isSpeedUpgradeOffer = (guid: string) =>
  [ElisaYrityspuheUusiOfferCode.VoiceYrityspuhePro1000M, ElisaYrityspuheUusiOfferCode.VoiceYrityspuhePlus300M].some(
    code => code === guid
  );
